import { useStaticSiteConfig } from "@commerce-frontend/ui/providers/StaticSiteConfigProvider";
import { useAuth } from "@labdigital/federated-token-react";
import { useClientGqlFetcher } from "@labdigital/graphql-fetcher";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FragmentOf, VariablesOf } from "gql.tada";
import { graphql } from "~/lib/graphql";
import { throwErrors } from "../lib/errors";
import { CartFragment, CartWithSelectorItemsFragment } from "./cart.fragments";

type Options = {
	recalculate?: boolean;
};

export const GetCartQuery = graphql(
	`
		query GetCart($storeContext: StoreContextInput!, $recalculate: Boolean) {
			cart(storeContext: $storeContext, recalculate: $recalculate) {
				...CartWithSelectorItemsFragment
			}
		}
	`,
	[CartWithSelectorItemsFragment],
);

export const CartLineItemsAddMutation = graphql(
	`
		mutation CartLineItemsAdd(
			$storeContext: StoreContextInput!
			$id: CartIdentifierInput
			$lineItems: [CartLineItemCreateInput!]!
		) {
			cartLineItemsAdd(
				storeContext: $storeContext
				id: $id
				lineItems: $lineItems
			) {
				...Cart
			}
		}
	`,
	[CartFragment],
);

export const CartLineItemsRemoveMutation = graphql(
	`
		mutation CartLineItemsRemove(
			$storeContext: StoreContextInput!
			$id: CartIdentifierInput!
			$lineItemIds: [String!]!
		) {
			cartLineItemsRemove(
				storeContext: $storeContext
				id: $id
				lineItemIds: $lineItemIds
			) {
				...Cart
			}
		}
	`,
	[CartFragment],
);

export const CartLineItemsUpdateMutation = graphql(
	`
		mutation CartLineItemsUpdate(
			$storeContext: StoreContextInput!
			$id: CartIdentifierInput!
			$lineItems: [CartLineItemUpdateInput!]!
		) {
			cartLineItemsUpdate(
				storeContext: $storeContext
				id: $id
				lineItems: $lineItems
			) {
				...Cart
			}
		}
	`,
	[CartFragment],
);

export const CartDiscountCodeAddMutation = graphql(
	`
		mutation CartDiscountCodeAdd(
			$storeContext: StoreContextInput!
			$id: CartIdentifierInput!
			$code: String!
		) {
			cartDiscountCodeAdd(storeContext: $storeContext, id: $id, code: $code) {
				...Cart
			}
		}
	`,
	[CartFragment],
);

export const CartDiscountCodeRemoveMutation = graphql(
	`
		mutation CartDiscountCodeRemove(
			$storeContext: StoreContextInput!
			$id: CartIdentifierInput!
			$discountCodeId: ID!
		) {
			cartDiscountCodeRemove(
				storeContext: $storeContext
				id: $id
				discountCodeId: $discountCodeId
			) {
				...Cart
			}
		}
	`,
	[CartFragment],
);

export const useCart = (options: Options = { recalculate: false }) => {
	const { currency, storeKey, locale } = useStaticSiteConfig();
	const storeContext = { currency, storeKey, locale };
	const gqlClientFetch = useClientGqlFetcher();
	const { hasToken, loading: authLoading } = useAuth();

	const queryKey = ["cart"] as const;

	const {
		data: cart,
		isLoading: queryIsLoading,
		...rest
	} = useQuery({
		queryKey,
		queryFn: () =>
			gqlClientFetch(GetCartQuery, {
				storeContext,
				...options,
			}).then((response) => response.data?.cart ?? null),
		enabled: hasToken,
		// Force a refetch if the recalculate option is true
		// Otherwise, the cart data is fetched once, not refetched on soft navigations,
		// but updated through cache updates in mutations.
		staleTime: options.recalculate ? 0 : Infinity,
	});

	const isLoading = authLoading || queryIsLoading;

	return { ...rest, cart, isLoading };
};

export const useUpdateLineItem = () => {
	const { currency, storeKey, locale } = useStaticSiteConfig();
	const storeContext = { currency, storeKey, locale };
	const client = useQueryClient();
	const gqlClientFetch = useClientGqlFetcher();

	const queryKey = ["cart"] as const;

	return useMutation({
		mutationFn: (
			variables: Omit<
				VariablesOf<typeof CartLineItemsUpdateMutation>,
				"storeContext"
			>,
		) =>
			gqlClientFetch(CartLineItemsUpdateMutation, {
				...variables,
				storeContext,
			}),
		onSuccess: (response) => {
			client.setQueryData(queryKey, response.data?.cartLineItemsUpdate ?? null);
			void client.invalidateQueries({ queryKey: ["paymentMethods"] });
		},
	});
};

export const useRemoveLineItems = () => {
	const { currency, storeKey, locale } = useStaticSiteConfig();
	const storeContext = { currency, storeKey, locale };
	const client = useQueryClient();
	const gqlClientFetch = useClientGqlFetcher();

	const queryKey = ["cart"] as const;

	return useMutation({
		mutationFn: (
			variables: Omit<
				VariablesOf<typeof CartLineItemsRemoveMutation>,
				"storeContext"
			>,
		) =>
			gqlClientFetch(CartLineItemsRemoveMutation, {
				...variables,
				storeContext,
			}),
		onSuccess: (response) => {
			client.setQueryData(queryKey, response.data?.cartLineItemsRemove ?? null);
		},
	});
};

export const useAddLineItems = () => {
	const { currency, storeKey, locale } = useStaticSiteConfig();
	const storeContext = { currency, storeKey, locale };
	const client = useQueryClient();
	const gqlClientFetch = useClientGqlFetcher();

	const queryKey = ["cart"];

	return useMutation({
		mutationFn: (
			variables: Omit<
				VariablesOf<typeof CartLineItemsAddMutation>,
				"storeContext"
			> & { cart?: FragmentOf<typeof CartFragment> | null },
		) => {
			const { cart } = variables;
			const cartId = cart?.id;

			const supplyChannelId = cart ? determineSupplyChannel(cart) : undefined;
			const lineItems = variables.lineItems.map((line) => ({
				supplyChannelId,
				...line,
			}));

			return gqlClientFetch(CartLineItemsAddMutation, {
				id: cartId,
				lineItems,
				supplyChannelId,
				storeContext,
			}).then(
				(res) => throwErrors(res.errors) ?? res.data?.cartLineItemsAdd ?? null,
			);
		},
		onSuccess: (cart) => {
			client.setQueryData(queryKey, cart);
		},
	});
};

export const useAddDiscountCode = () => {
	const { currency, storeKey, locale } = useStaticSiteConfig();
	const storeContext = { currency, storeKey, locale };
	const client = useQueryClient();
	const gqlClientFetch = useClientGqlFetcher();

	const queryKey = ["cart"] as const;

	return useMutation({
		mutationFn: (
			variables: Omit<
				VariablesOf<typeof CartDiscountCodeAddMutation>,
				"storeContext"
			>,
		) =>
			gqlClientFetch(CartDiscountCodeAddMutation, {
				...variables,
				storeContext,
			}).then((response) => response),
		onSuccess: (response) => {
			const cart = response.data?.cartDiscountCodeAdd;
			if (cart) {
				client.setQueryData(queryKey, cart);
			}
		},
	});
};

export const useRemoveDiscountCode = () => {
	const { currency, storeKey, locale } = useStaticSiteConfig();
	const storeContext = { currency, storeKey, locale };
	const client = useQueryClient();
	const gqlClientFetch = useClientGqlFetcher();

	const queryKey = ["cart"] as const;

	return useMutation({
		mutationFn: (
			variables: Omit<
				VariablesOf<typeof CartDiscountCodeRemoveMutation>,
				"storeContext"
			>,
		) =>
			gqlClientFetch(CartDiscountCodeRemoveMutation, {
				...variables,
				storeContext,
			}),
		onSuccess: (response) => {
			client.setQueryData(
				queryKey,
				response.data?.cartDiscountCodeRemove ?? null,
			);
		},
	});
};

// TODO: Remove this function; this is handled by the setShippingInfo mutation and the
// setLineItemSupplyChannel API extension.
function determineSupplyChannel(cart: FragmentOf<typeof CartFragment>) {
	// Either all line items have the same supply channel or none have it
	const supplyChannel = cart?.lineItems?.[0]?.supplyChannel;
	if (supplyChannel) {
		return supplyChannel.id;
	}
	return undefined;
}
